import Workspace, { WorkspaceRole } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";

export const EMPTY_WORKSPACE_OWNER_ID = "00000000-0000-0000-0000-000000000000";

export const isOrphanedWorkspace = (workspace: Workspace): boolean =>
  !workspace.userId || workspace.userId === EMPTY_WORKSPACE_OWNER_ID;

/** Aligns with API WorkspaceRole enum order (Read=0, Write=1, Admin=2). */
const SHARED_ROLE_RANK: Record<
  WorkspaceRole.READ | WorkspaceRole.WRITE | WorkspaceRole.ADMIN,
  number
> = {
  [WorkspaceRole.READ]: 0,
  [WorkspaceRole.WRITE]: 1,
  [WorkspaceRole.ADMIN]: 2,
};

const mergeSharedRoles = (
  individual?: WorkspaceRole,
  tenant?: WorkspaceRole
): WorkspaceRole | null => {
  if (!individual && !tenant) return null;
  if (!individual) return tenant ?? null;
  if (!tenant) return individual;
  return SHARED_ROLE_RANK[individual] >= SHARED_ROLE_RANK[tenant]
    ? individual
    : tenant;
};

export const isWorkspaceOwner = (
  workspace: Workspace,
  userId: string
): boolean => workspace.userId === userId;

/**
 * Effective role from sharing (individual grant + tenant SharedWorkspaceRole via max).
 * Returns null for owner (use isWorkspaceOwner) or when user has no shared role.
 * Mirrors API HasSharedAccess role resolution.
 */
export const getEffectiveWorkspaceRole = (
  workspace: Workspace,
  userId: string
): WorkspaceRole | null => {
  if (isWorkspaceOwner(workspace, userId)) return null;

  const individual = workspace.sharedAccess?.[userId];
  const tenant = workspace.sharedWithWholeTenant
    ? workspace.sharedWorkspaceRole
    : undefined;

  return mergeSharedRoles(individual, tenant);
};

/** Display role for header badge (owner → OWNER). */
export const getDisplayWorkspaceRole = (
  workspace: Workspace,
  userId: string
): WorkspaceRole => {
  if (isWorkspaceOwner(workspace, userId)) return WorkspaceRole.OWNER;
  return getEffectiveWorkspaceRole(workspace, userId) ?? WorkspaceRole.READ;
};

export const hasSharedAccess = (
  workspace: Workspace,
  userId: string,
  minimumRole: WorkspaceRole
): boolean => {
  const effective = getEffectiveWorkspaceRole(workspace, userId);
  if (!effective) return false;
  return SHARED_ROLE_RANK[effective] >= SHARED_ROLE_RANK[minimumRole];
};

export const canUploadFiles = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  return (
    isWorkspaceOwner(workspace, userId) ||
    hasSharedAccess(workspace, userId, WorkspaceRole.WRITE)
  );
};

export const canEditMetadata = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  return (
    isWorkspaceOwner(workspace, userId) ||
    hasSharedAccess(workspace, userId, WorkspaceRole.WRITE)
  );
};

/**
 * Moving is a write on both the source and the target, so it follows the same
 * rule as uploading: owner or shared Write. Kept separate so the call sites
 * read as what they mean, not as a borrowed permission.
 */
export const canMoveItems = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  return (
    isWorkspaceOwner(workspace, userId) ||
    hasSharedAccess(workspace, userId, WorkspaceRole.WRITE)
  );
};

export const canDeleteItem = (
  workspace: Workspace,
  userId: string,
  item: Item,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  if (isWorkspaceOwner(workspace, userId)) return true;

  if (hasSharedAccess(workspace, userId, WorkspaceRole.ADMIN)) return true;

  if (item.type === "folder") return false;

  if (
    hasSharedAccess(workspace, userId, WorkspaceRole.WRITE) &&
    item.userId === userId
  ) {
    return true;
  }

  return false;
};

export const isWorkspaceAdmin = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  return (
    isWorkspaceOwner(workspace, userId) ||
    hasSharedAccess(workspace, userId, WorkspaceRole.ADMIN)
  );
};

export const canViewSettings = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => isWorkspaceAdmin(workspace, userId, isParentWorkspace);

export const canReindexItem = (
  workspace: Workspace,
  userId: string,
  isParentWorkspace: boolean
): boolean => {
  if (isParentWorkspace) return false;
  return (
    isWorkspaceOwner(workspace, userId) ||
    hasSharedAccess(workspace, userId, WorkspaceRole.WRITE)
  );
};
